import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  User,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase-production.config';

export interface SignUpData {
  email: string;
  password: string;
  displayName: string;
  role?: 'admin' | 'manager' | 'measurer' | 'client';
  companyId?: string;
  phone?: string;
}

export interface UserData {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role: 'admin' | 'manager' | 'measurer' | 'client';
  companyId?: string;
  phone?: string;
  active: boolean;
  emailVerified: boolean;
  createdAt: unknown;
  updatedAt: unknown;
}

// Sign up a new user
export const signUpUser = async (data: SignUpData): Promise<User> => {
  try {
    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
    const user = userCredential.user;

    // Update display name
    await updateProfile(user, {
      displayName: data.displayName,
    });

    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: data.email,
      displayName: data.displayName,
      photoURL: null,
      role: data.role || 'client',
      companyId: data.companyId || null,
      phone: data.phone || null,
      active: true,
      emailVerified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Send email verification
    await sendEmailVerification(user);

    return user;
  } catch (error) {
    console.error('Error signing up user:', error);
    throw error;
  }
};

// Sign in a user
export const signInUser = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if user is active
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (!userData.active) {
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact support.');
      }

      // Update last login
      await updateDoc(doc(db, 'users', user.uid), {
        lastLoginAt: serverTimestamp(),
        emailVerified: user.emailVerified,
      });
    }

    return user;
  } catch (error) {
    console.error('Error signing in user:', error);
    throw error;
  }
};

// Sign out the current user
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out user:', error);
    throw error;
  }
};

// NB: no sendPasswordReset helper here on purpose. The client SDK's
// sendPasswordResetEmail delivers through Google's mailer from
// noreply@<project>.firebaseapp.com, which bypasses our SendGrid domain
// authentication and silently failed to arrive. Password recovery goes through
// the `requestPasswordReset` server action instead
// (src/app/(auth)/forgot-password/actions.ts).

// Update user password
export const updateUserPassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No authenticated user found');
    }

    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('Error updating password:', error);
    throw error;
  }
};

// Update user profile
export const updateUserProfile = async (updates: {
  displayName?: string;
  photoURL?: string;
  phone?: string;
}): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    // Update Firebase Auth profile
    if (updates.displayName !== undefined || updates.photoURL !== undefined) {
      await updateProfile(user, {
        displayName: updates.displayName ?? user.displayName,
        photoURL: updates.photoURL ?? user.photoURL,
      });
    }

    // Update Firestore document
    const firestoreUpdates: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
    };

    if (updates.displayName !== undefined) {
      firestoreUpdates.displayName = updates.displayName;
    }
    if (updates.photoURL !== undefined) {
      firestoreUpdates.photoURL = updates.photoURL;
    }
    if (updates.phone !== undefined) {
      firestoreUpdates.phone = updates.phone;
    }

    await updateDoc(doc(db, 'users', user.uid), firestoreUpdates);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Get current user data from Firestore
export const getCurrentUserData = async (): Promise<UserData | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      return null;
    }

    return userDoc.data() as UserData;
  } catch (error) {
    console.error('Error getting current user data:', error);
    throw error;
  }
};

// Check if user has a specific role
export const hasRole = async (role: UserData['role']): Promise<boolean> => {
  try {
    const userData = await getCurrentUserData();
    return userData?.role === role || false;
  } catch (error) {
    console.error('Error checking user role:', error);
    return false;
  }
};

// Check if user is admin
export const isAdmin = async (): Promise<boolean> => {
  return hasRole('admin');
};

// Check if user is manager
export const isManager = async (): Promise<boolean> => {
  return hasRole('manager');
};

// Check if user is measurer
export const isMeasurer = async (): Promise<boolean> => {
  return hasRole('measurer');
};

// Check if user is client
export const isClient = async (): Promise<boolean> => {
  return hasRole('client');
};

// Resend email verification
export const resendEmailVerification = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    await sendEmailVerification(user);
  } catch (error) {
    console.error('Error resending email verification:', error);
    throw error;
  }
};
